import { useCallback, useEffect, useRef, useState } from "react";
import {
  fetchFeed,
  nextPage,
  resetFeed,
  selectFeedHasMore,
  selectFeedPage,
  selectFeedPosts,
  selectFeedStatus,
} from "store/feed/feed.slice";
import { useAppDispatch, useAppSelector } from "store/hooks";
import {
  BottomSection,
  CardContainer,
  CommentsAndShares,
  Content,
  ContentPicture,
  ImageContainer,
  Likes,
  NameAndDate,
  OrganizationName,
  OrgInformation,
  PostActions,
  PostDetails,
  ProfilePicture,
  TimePosted,
} from "./PostCard.styles";
import { filterPosts, formatTime, highlightText } from "./PostFiltering";

import { useNavigate } from "react-router-dom";
import { User } from "types";
import NoEventsPlaceholder from "views/OrganizationProfile/Events/TabEvents/NoEventsPlaceholder";
import CommentAction from "./CommentAction";
import LikeAction from "./LikeAction";
import { FeedSkeletonCard } from "./PostLoading";
import SaveAction from "./SaveAction";
import ShareAction from "./ShareAction";

const PostCard = ({ searchText, filter, publicUserId }: PostCardProps) => {
  const dispatch = useAppDispatch();

  const posts = useAppSelector(selectFeedPosts);
  const hasMore = useAppSelector(selectFeedHasMore);
  const status = useAppSelector(selectFeedStatus);
  const page = useAppSelector(selectFeedPage);
  const [filteredPosts, setFilteredPosts] = useState<Post[]>([]);
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);
  const navigate = useNavigate();
  const observer = useRef<IntersectionObserver | null>(null);
  const isLoading = status === "loading";
  const hasPosts = filteredPosts.length > 0;
  const isFirstLoad = isLoading && !hasPosts;
  const isNextPageLoad = isLoading && hasPosts;
  const mustShowError = status === "failed" && !isLoading && !hasPosts;
  const sentinelRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (!hasMore || status !== "succeeded") return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          dispatch(nextPage());
        }
      });

      if (node) observer.current.observe(node);
    },
    [hasMore, status, dispatch]
  );

  useEffect(() => {
    dispatch(resetFeed());
  }, [publicUserId]);

  useEffect(() => {
    if (status === "idle") {
      dispatch(fetchFeed(publicUserId ? { publicUserId } : undefined));
    }
  }, [status, dispatch, publicUserId]);

  useEffect(() => {
    dispatch(fetchFeed(publicUserId ? { publicUserId } : undefined));
  }, [dispatch, page, publicUserId]);

  useEffect(() => {
    const updatedFilteredPosts = filterPosts(posts, searchText, filter);
    setFilteredPosts(updatedFilteredPosts);
  }, [posts, searchText, filter]);

  const handlePostClick = (data: Post) => {
    navigate(`/post/${data.postId}`);
  };

  const handleProfileClick = (userId: User["id"]) => {
    navigate(`/profile/${userId}`);
  };

  useEffect(() => {
    if (selectedPost) {
      const updatedPost = posts.find(
        (post: any) => post.postId === selectedPost.postId
      );
      if (updatedPost) {
        setSelectedPost(updatedPost);
      }
    }
  }, [posts, selectedPost]);

  return (
    <>
      {hasPosts &&
        filteredPosts.map((data, index) => (
          <CardContainer key={index}>
            <OrgInformation>
              <ProfilePicture
                src={data.profilePictureURL}
                onClick={() => handleProfileClick(data.postOwnerId)}
                style={{ cursor: 'pointer' }}
              />
              <NameAndDate>
                <OrganizationName
                  onClick={() => handleProfileClick(data.postOwnerId)}
                  style={{ cursor: 'pointer' }}
                >
                  {highlightText(data.organizationName, searchText)}
                </OrganizationName>
                <TimePosted>{formatTime(data.time)}</TimePosted>
              </NameAndDate>
              <Content>{highlightText(data.description, searchText)}</Content>
            </OrgInformation>
            {data.contentPicture && (
              <ImageContainer>
                <ContentPicture
                  src={data.contentPicture}
                  alt="Post content"
                  onClick={() => handlePostClick(data)}
                />
              </ImageContainer>
            )}
            <BottomSection>
              <PostDetails>
                <Likes>
                  <div>
                    {data.likesProfiles?.slice(0, 3).map((user, index) => (
                      <img
                        key={index}
                        src={user.profilePictureURL}
                        alt={`User ${index + 1}`}
                      />
                    ))}
                  </div>
                  <span>
                    {data.likesCount} Like{data.likesCount === 1 ? "" : "s"}
                  </span>
                </Likes>
                <CommentsAndShares>
                  <span
                    onClick={() => handlePostClick(data)}
                    style={{ cursor: "pointer" }}
                  >
                    {data.comments} Comment{data.comments === 1 ? "" : "s"} •{" "}
                    {data.shares} Share{data.shares === 1 ? "" : "s"}
                  </span>
                </CommentsAndShares>
              </PostDetails>
              <PostActions>
                <LikeAction
                  postId={data.postId}
                  postOwnerId={data.postOwnerId}
                  isLiked={data.isCurrentLiked}
                />

                <CommentAction
                  post={data}
                  handlePostClick={() => {
                    handlePostClick(data);
                  }}
                />

                <ShareAction />

                <SaveAction />
              </PostActions>
            </BottomSection>
          </CardContainer>
        ))}
      {isFirstLoad && [...Array(3)].map((_, i) => <FeedSkeletonCard key={i} />)}
      {isNextPageLoad && <FeedSkeletonCard />}
      {!isLoading && !hasPosts && !mustShowError && (
        <NoEventsPlaceholder message="No posts found" />
      )}
      {mustShowError && <NoEventsPlaceholder message={"No posts found"} />}

      {/* {showModal && selectedPost && currentUserId !== undefined && (
        <PostComments
          post={selectedPost}
          setPosts={setPosts}
          setSelectedPost={setSelectedPost}
          showModal={showModal}
          setShowModal={setShowModal}
          currentUserId={currentUserId}
        />
      )} */}

      <div ref={sentinelRef} style={{ height: 1 }} />
      {hasPosts && !hasMore && (
        <p style={{ textAlign: "center" }}>No more posts</p>
      )}
    </>
  );
};

export default PostCard;
